<template>
  <ValidationObserver
    ref="bulkAddToGroupModal"
  >
    <b-modal
      id="bulk-add-to-group-modal"
      ref="bulk-add-to-group-modal"
      centered
      title="Bulk Add To Group"
      ok-title="Save"
      ok-only
      no-close-on-backdrop
      @hidden="resetModal"
      @show="preparePlaceholdersData"
    >
      <b-overlay
        id="overlay-background"
        :show="isLoading"
        variant="white"
        opacity="1"
        rounded="sm"
      >
        <b-row>
          <b-col
            v-if="withExecute"
            sm="12"
            class="mb-1"
          >
            <b-form-group
              label="Field with available application ID"
            >
              <validation-provider
                #default="{ errors }"
                name="destination field"
                rules="required"
              >
                <v-select
                  v-model="targetField"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="destinationTypeOptions"
                  :reduce="option => option.value"
                  :clearable="false"
                  :placeholder="'Select Field'"
                  :disabled="!destinationTypeOptions.length"
                  :class="{'is-invalid': errors.length > 0 }"
                  @input="handleTargetIdSelected"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col
            sm="12"
            class="mb-1"
          >
            <b-form-group
              label="Group"
            >
              <validation-provider
                #default="{ errors }"
                name="group"
                rules="required"
              >
                <v-select
                  v-model="selectedGroup"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="availableGroupsList"
                  label="name"
                  value="id"
                  :clearable="false"
                  :placeholder="fieldGroupStatus"
                  :disabled="!targetField && withExecute"
                  :class="{'is-invalid': errors.length > 0 }"
                  @input="handleGroupSelected"
                >
                  <template
                    #option="{ name, type }"
                  >
                    <div class="d-flex justify-content-between align-items-center">
                      <span>{{ name }}</span> <em class="mr-2">{{ type }}</em>
                    </div>
                  </template>
                </v-select>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col
            v-if="selectedGroup"
            sm="12"
            class="mb-1"
          >
            <b-form-group
              label="Session"
            >
              <validation-provider
                #default="{ errors }"
                name="session"
                rules="required"
              >
                <v-select
                  v-model="selectedSession"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="availableSessionsListBySelectedGroup"
                  multiple
                  label="name"
                  value="id"
                  :clearable="false"
                  :placeholder="'Select Session'"
                  :reduce="session => session.id"
                  :class="{'is-invalid': errors.length > 0 }"
                  @input="handleSessionSelected"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col
            v-if="isUsersLoading"
            sm="12"
            class="mb-1 d-flex justify-content-center align-items-center"
          >
            <span class="text-muted mr-1">Fetching Available Users</span> <b-spinner />
          </b-col>

          <b-col
            v-if="usersListReadyForAdding.length"
            sm="12"
            class="mb-1"
          >
            <app-collapse>
              <app-collapse-item
                :title="`Available Users (${usersListReadyForAdding.length})`"
              >
                <div
                  v-for="application in usersListReadyForAdding"
                  :key="application.id"
                  class="mb-1"
                >
                  {{ application.user.full_name }}
                </div>
              </app-collapse-item>
            </app-collapse>
          </b-col>
        </b-row>
      </b-overlay>

      <template #modal-footer>
        <span class="mr-1 text-muted">{{ usersListReadyForAdding.length }} Available Users</span>
        <b-button
          v-ripple.400="'rgba(186, 191, 199, 0.15)'"
          variant="outline-secondary"
          :disabled="isLoading"
          @click="closeModal"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          class="float-right"
          :disabled="isLoading || !usersListReadyForAdding.length"
          @click="handleForm"
        >
          Save
        </b-button>
      </template>
    </b-modal>
  </ValidationObserver>
</template>

<script>
import {
  BModal,
  BRow,
  BCol,
  BFormGroup,
  BOverlay,
  BButton,
  BSpinner,
} from 'bootstrap-vue'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import useBulkAddGroups from '@/views/components/bulk-add-to-group/useBulkAddGroups'
import useReportDetails from '@/views/admin/reports-v2/reports/report-details/useReportDetails'

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    AppCollapse,
    AppCollapseItem,
    BModal,
    BRow,
    BCol,
    BFormGroup,
    BOverlay,
    BButton,
    BSpinner,
    vSelect,
  },
  directives: {
    Ripple,
  },
  props: {
    markedElements: {
      type: Array,
      required: true,
    },
    fieldsOptions: {
      type: [Array, Object],
      default: () => {},
    },
    executeData: {
      type: Object,
      default: () => {},
    },
    allElementsChecked: {
      type: Boolean,
      default: () => false,
    },
    withExecute: {
      type: Boolean,
      default: () => false,
    },
    filterName: {
      type: String,
      default: null,
    },
    groupId: {
      type: String,
      default: '',
    },
    programId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      destinationTypeOptions: [],
      selectedGroup: null,
      selectedApplications: null,
      selectedSession: null,
      targetField: null,
      availableGroupsList: [],
      availableSessionsList: [],
      availableSessionsListBySelectedGroup: [],
      isLoading: false,
      isUsersLoading: false,
      fieldGroupStatus: 'Select Group',
      usersListReadyForAdding: [],
      required,
    }
  },
  setup() {
    const {
      fetchSessions,
      fetchGroups,
      fetchUsers,
      addUsersToGroup,
      addApplicationsToGroup,
    } = useBulkAddGroups()

    const {
      fetchResultsByTargetField,
    } = useReportDetails()

    return {
      fetchSessions,
      fetchGroups,
      fetchUsers,
      addUsersToGroup,
      addApplicationsToGroup,
      fetchResultsByTargetField,
    }
  },
  methods: {
    closeModal() {
      this.$refs['bulk-add-to-group-modal'].hide()
    },
    async preparePlaceholdersData() {
      if (this.withExecute) {
        this.destinationTypeOptions = this.fieldsOptions
      } else {
        const sessionFilterParams = {}
        if (this.filterName) {
          sessionFilterParams[this.filterName] = this.markedElements
        }
        await this.prepareSessionsAndGroups(sessionFilterParams)
      }
    },
    resetModal() {
      this.destinationTypeOptions = []
      this.selectedGroup = null
      this.selectedApplications = null
      this.selectedSession = null
      this.targetField = null
      this.availableGroupsList = []
      this.availableSessionsList = []
      this.usersListReadyForAdding = []
      this.availableSessionsListBySelectedGroup = []
    },
    async handleTargetIdSelected() {
      this.availableGroupsList = []
      this.selectedGroup = null
      this.selectedSession = []
      this.usersListReadyForAdding = []

      if (this.allElementsChecked) {
        const queryParams = {
          executeData: this.executeData,
          targetField: this.targetField,
        }
        if (this.programId) {
          queryParams.program_id = this.programId
        }
        this.selectedApplications = await this.fetchResultsByTargetField(queryParams)
          .then(response => response.data)
      } else {
        this.selectedApplications = this.markedElements.map(element => element[this.targetField])
      }

      const sessionFilterParams = { applicationId: this.selectedApplications }
      await this.prepareSessionsAndGroups(sessionFilterParams)
    },
    async prepareSessionsAndGroups(sessionFilterParams = {}) {
      this.availableSessionsList = await this.fetchSessions(sessionFilterParams)
        .then(response => response.reduce((arr, item) => {
          arr.push({ value: item, text: item.name })
          return arr
        }, []))

      if (this.availableSessionsList.length) {
        const formData = {
          exceptProgramType: 'Program',
          sessionId: this.availableSessionsList.map(option => option.value.id),
        }
        if (this.programId) {
          formData.programId = this.programId
        }

        this.fieldGroupStatus = 'Loading...'
        this.availableGroupsList = await this.fetchGroups(formData).then(res => res)
        this.fieldGroupStatus = 'Select Group'
      }
    },
    async fetchSessionsByGroup() {
      const formData = {
        groupId: this.selectedGroup?.id,
      }
      this.availableSessionsListBySelectedGroup = await this.fetchSessions(formData)
    },
    async handleGroupSelected() {
      this.selectedSession = []
      this.usersListReadyForAdding = []
      this.availableSessionsListBySelectedGroup = []
      await this.fetchSessionsByGroup()
    },
    handleSessionSelected() {
      if (this.allElementsChecked && this.groupId) {
        this.handleSessionSelectedForAllChecked()
      } else {
        this.handleSessionSelectedForMarkedElements()
      }
    },
    async handleSessionSelectedForMarkedElements() {
      this.usersListReadyForAdding = []
      this.isUsersLoading = true
      const formData = {
        applicationId: this.selectedApplications,
        sessionId: this.selectedSession,
      }
      if (this.filterName) {
        formData[this.filterName] = this.markedElements
      }

      await this.fetchUsers(formData).then(response => {
        this.usersListReadyForAdding = response
      })
    },
    async handleSessionSelectedForAllChecked() {
      this.usersListReadyForAdding = []

      const formData = {
        groupId: this.groupId,
        sessionId: this.selectedSession,
      }

      await this.fetchUsers(formData).then(response => {
        this.usersListReadyForAdding = response
      })
    },
    handleForm() {
      return new Promise((resolve, reject) => {
        this.$refs.bulkAddToGroupModal.validate().then(success => {
          if (success) {
            resolve(true)
            this.isLoading = true
            this.setGroups()
          } else {
            reject()
          }
        })
      })
    },
    async setGroups() {
      const queryParams = {
        group_id: this.selectedGroup.id,
        application_id: this.usersListReadyForAdding.map(user => user.id),
        sessions: this.selectedSession,
      }
      this.isLoading = true
      await this.addApplicationsToGroup(queryParams)
        .then(response => {
          if (response.status === 200) {
            this.$emit('successfullySend')
          }
        })
        .finally(() => {
          this.isLoading = false
        })
    },
  },
}
</script>
